import React, { useEffect, useCallback, useState } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import toastr from "toastr";

import { makeStyles } from "@material-ui/core/styles";

import Container from "@material-ui/core/Container";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import Grid from "@material-ui/core/Grid";

import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CardBody from "components/Card/CardBody";
import CardText from "components/Card/CardText";

import Button from "components/CustomButtons/Button";

import { actionCreators as actionCreatorsAdmin } from "../../../../_actions/adminActions";
import Loading from "../../../common/Loading";
import EnhancedTableHead, {
  stableSort,
  getComparator
} from "../../../common/EnhancedTableHead";
import MemberCountDialog from "./EditDialog";
import { PAGE_SIZE } from "../../../../constants";
import { yearList } from "../../../../utils";
import FileUploadButton from "../../../common/FileUploadButton";

const useStyles = makeStyles(theme => ({
  root: {
    width: "100%"
  },
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2)
  },
  table: {
    minWidth: 750
  },
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1
  },
  marginBottom: {
    marginBottom: theme.spacing(3)
  },
  cursorPointer: {
    cursor: "pointer"
  },
  menuPaper: {
    maxHeight: 300
  },
  formControl: {
    minWidth: 150
  },
  fullWidth: {
    flex: 1
  }
}));

const headCells = [
  {
    id: "year",
    numeric: false,
    disablePadding: false,
    label: "Year"
  },
  {
    id: "chapterName",
    numeric: false,
    disablePadding: false,
    label: "Chapter Name"
  },
  {
    id: "count",
    numeric: false,
    disablePadding: false,
    label: "Count"
  },
  {
    id: "updatedDate",
    numeric: true,
    disablePadding: false,
    label: "Updated Date"
  },
  {
    id: "submittedBy",
    numeric: true,
    disablePadding: false,
    label: "Submitted By"
  }
];

const sortBy = {
  year: 1,
  chapterName: 2,
  count: 3,
  updatedDate: 4,
  submittedBy: 5
};

const MemberCounts = props => {
  const classes = useStyles();
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("year");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(
    parseInt(localStorage.getItem("memberCountsPageSize")) || PAGE_SIZE
  );
  const [chapter, setChapter] = useState(-1);
  const [year, setYear] = useState(-1);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [isEditing, setEditing] = useState(false);
  const [selectedRow, setSelectedRow] = useState("");

  const {
    getMemberCounts,
    getChapterList,
    createMemberCount,
    updateMemberCount,
    chapterList,
    memberCounts,
    totalLength,
    isError,
    errorMessage,
    isFetched,
    isFetching
  } = props;

  useEffect(() => {
    if (isError && errorMessage) {
      toastr.error(errorMessage);
    }
  }, [isError, errorMessage]);

  const fetchMemberCounts = useCallback(() => {
    if (!isFetching.MEMBER_COUNTS) {
      getMemberCounts(
        rowsPerPage,
        page * rowsPerPage,
        chapter,
        year,
        order,
        sortBy[orderBy]
      );
    }
  }, [
    getMemberCounts,
    rowsPerPage,
    page,
    chapter,
    year,
    orderBy,
    order,
    isFetching
  ]);

  useEffect(() => {
    localStorage.setItem("memberCountsPageSize", rowsPerPage);
  }, [rowsPerPage]);

  useEffect(() => {
    if (!isFetched.MEMBER_COUNTS && !isFetching.MEMBER_COUNTS) {
      fetchMemberCounts();
    }
    if (!isFetched.CHAPTER_LIST && !isFetching.CHAPTER_LIST) {
      getChapterList();
    }
  }, []);

  useEffect(() => {
    if (!isFetching.MEMBER_COUNTS) {
      getMemberCounts(
        rowsPerPage,
        page * rowsPerPage,
        chapter,
        year,
        order,
        sortBy[orderBy]
      );
    }
  }, [getMemberCounts, rowsPerPage, page, chapter, year, orderBy, order]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleEditTopic = useCallback(
    row => () => {
      setSelectedRow(row);
      setDialogOpen(true);
      setEditing(true);
    },
    []
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChapterChange = useCallback(({ target: { value } }) => {
    setChapter(value);
  }, []);

  const handleYearChange = useCallback(({ target: { value } }) => {
    setYear(value);
  }, []);

  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const handleCreateDialogOpen = useCallback(() => {
    setDialogOpen(true);
    setEditing(false);
  }, []);

  const handleDialogSave = useCallback(
    async (chapter, fiscalYear, count) => {
      setDialogOpen(false);
      const payload = {
        chapter: {
          id: chapter
        },
        year: fiscalYear,
        count
      };
      if (isEditing) {
        await updateMemberCount({
          ...payload,
          id: selectedRow.id
        });
      } else {
        await createMemberCount(payload);
      }
      fetchMemberCounts();
    },
    [
      createMemberCount,
      updateMemberCount,
      isEditing,
      selectedRow,
      fetchMemberCounts
    ]
  );

  const getStandardDate = useCallback(dateString => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US");
  }, []);

  return (
    <Container>
      <Card>
        <CardHeader color="primary" text>
          <CardText color="primary">
            <Typography variant="h4">
              Chapter Member Count Management
            </Typography>
          </CardText>
        </CardHeader>
        <CardBody>
          <Paper className={classes.paper}>
            <Grid
              container
              className={classes.marginBottom}
              spacing={2}
              justify="flex-end"
            >
              <Grid item className={classes.fullWidth}>
                <Button color="success" onClick={handleCreateDialogOpen}>
                  Submit Member Count
                </Button>
                <FileUploadButton
                  downloadTemplate={props.getMemberCountTemplate}
                  uploadFile={props.uploadMemberCounts}
                  isUploadSuccess={props.isUploadSuccess}
                  isError={props.isError}
                  message={props.message}
                />
              </Grid>
              <Grid item>
                <FormControl variant="outlined" className={classes.formControl}>
                  <InputLabel id="chapter-label">Chapter</InputLabel>
                  <Select
                    labelId="chapter-label"
                    id="demo-simple-select-outlined"
                    value={chapter}
                    onChange={handleChapterChange}
                    label="Chapter"
                    MenuProps={{ classes: { paper: classes.menuPaper } }}
                  >
                    <MenuItem value={-1}>
                      <em>None</em>
                    </MenuItem>
                    {chapterList &&
                      chapterList.map((x, index) => (
                        <MenuItem value={x.id} key={`chapter-key-${index}`}>
                          {x.name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item>
                <FormControl variant="outlined" className={classes.formControl}>
                  <InputLabel id="year-label">Year</InputLabel>
                  <Select
                    labelId="year-label"
                    id="demo-simple-select-outlined"
                    value={year}
                    onChange={handleYearChange}
                    label="Year"
                    MenuProps={{ classes: { paper: classes.menuPaper } }}
                  >
                    <MenuItem value={-1}>
                      <em>None</em>
                    </MenuItem>
                    {yearList.map((x, index) => (
                      <MenuItem value={x} key={`chapter-key-${index}`}>
                        {x}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <TableContainer>
              <Table
                className={classes.table}
                aria-labelledby="tableTitle"
                size="medium"
                aria-label="enhanced table"
              >
                <EnhancedTableHead
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleRequestSort}
                  rowCount={totalLength}
                  headCells={headCells}
                />

                <TableBody>
                  {isFetching.MEMBER_COUNTS ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Loading />
                      </TableCell>
                    </TableRow>
                  ) : !memberCounts || memberCounts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No data
                      </TableCell>
                    </TableRow>
                  ) : (
                    stableSort(memberCounts, getComparator(order, orderBy)).map(
                      row => {
                        const labelId = `enhanced-table-row-${row.id}`;

                        return (
                          <TableRow
                            hover
                            className={classes.cursorPointer}
                            onClick={handleEditTopic(row)}
                            role="checkbox"
                            tabIndex={-1}
                            key={labelId}
                          >
                            <TableCell component="th" scope="row">
                              {row.year}
                            </TableCell>
                            <TableCell component="th" scope="row">
                              {row.chapter.name}
                            </TableCell>
                            <TableCell component="th" scope="row">
                              {row.count}
                            </TableCell>
                            <TableCell align="right">
                              {getStandardDate(row.updatedDate)}
                            </TableCell>
                            <TableCell align="right">
                              {row.updatedByUserName}
                            </TableCell>
                          </TableRow>
                        );
                      }
                    )
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              component="div"
              count={totalLength}
              rowsPerPage={rowsPerPage}
              page={page}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
            />
            <MemberCountDialog
              open={isDialogOpen}
              editing={isEditing}
              chapterList={chapterList}
              chapterId={
                isEditing && selectedRow
                  ? selectedRow.chapter.id
                  : !isEditing
                  ? chapter
                  : ""
              }
              year={
                isEditing && selectedRow
                  ? selectedRow.year
                  : !isEditing
                  ? year
                  : ""
              }
              originalCount={isEditing && selectedRow ? selectedRow.count : ""}
              onClose={handleDialogClose}
              onSave={handleDialogSave}
            />
          </Paper>
        </CardBody>
      </Card>
    </Container>
  );
};

const mapStateToProps = state => ({
  isLoading: state.adminReducer.isLoading,
  memberCounts: state.adminReducer.memberCounts,
  totalLength: state.adminReducer.totalMemberCounts,
  chapterList: state.adminReducer.chapterList,
  isError: state.adminReducer.isError,
  isUploadSuccess: state.adminReducer.isUploadSuccess,
  isFetched: state.adminReducer.isFetched,
  isFetching: state.adminReducer.isFetching,
  message: state.adminReducer.message
});

const mapDispatchToProps = dispatch =>
  bindActionCreators({ ...actionCreatorsAdmin }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(MemberCounts);
